import { IShape2d, IShapeSeg2d } from '@/editor/types/shape';
import { Line2d } from '../Line2d';
import { IPoint2d } from '../IPoint2d';
import { LineSeg2d } from '../LineSeg2d';
import { ArcSeg2d } from '../arcSeg2d';
import { Point2dUtils } from '../Point2dUtils';
import { normalizeAngle } from '../utils';
import { Ray2d } from '../ray2d';
import { Circle2d } from '../circle2d';
import { thresholdValue } from '../const';
import { isArc } from '@/editor/utils/arc';

export function isRightAngleShape(shape1: IShape2d, shape2: IShape2d): boolean {
  if (shape1 instanceof Line2d && shape2 instanceof Line2d) {
    return shape1.isRightAngle(shape2);
  }

  return false;
}

export function isTangentShape(shape1: IShape2d, shape2: IShape2d): boolean {
  if (shape1 instanceof Line2d && shape2 instanceof Line2d) {
    return shape1.isParallelTo(shape2);
  }

  if (shape1 instanceof Circle2d && shape2 instanceof Line2d) {
    [shape1, shape2] = [shape2, shape1];
  }

  if (shape1 instanceof Line2d && shape2 instanceof Circle2d) {
    const lintToCenterDistance = shape1.pointToLineDistance(shape2.center);
    return Math.abs(lintToCenterDistance - shape2.radius) < thresholdValue;
  }

  if (
    (shape1 instanceof Circle2d && shape2 instanceof Circle2d) ||
    (shape1 instanceof ArcSeg2d && shape2 instanceof ArcSeg2d)
  ) {
    const distance = Point2dUtils.distanceTo(shape1.center, shape2.center);

    // 外切
    if (Math.abs(distance - shape1.radius - shape2.radius) < thresholdValue) return true;

    // 内切
    if (Math.abs(distance - Math.abs(shape1.radius - shape2.radius)) < thresholdValue) return true;
  }

  return false;
}

export function pointOnSeg(seg: IShapeSeg2d, point: IPoint2d): boolean {
  if (seg instanceof LineSeg2d) {
    return seg.pointOnSegment(point);
  } else if (seg instanceof ArcSeg2d) {
    return seg.pointOnSegment(point);
  }
  return false;
}

export function pointOnShape(shape: IShape2d, point: IPoint2d): boolean {
  if (shape instanceof LineSeg2d) {
    return shape.pointOnSegment(point);
  } else if (shape instanceof ArcSeg2d) {
    return shape.pointOnSegment(point);
  } else if (shape instanceof Ray2d) {
    return shape.pointOnRay(point);
  } else if (shape instanceof Circle2d) {
    return Math.abs(Point2dUtils.distanceTo(shape.center, point) - shape.radius) < thresholdValue;
  } else if (shape instanceof Line2d) {
    return shape.pointOnLine(point);
  }
  return false;
}

export function getFootRatio(seg: IShapeSeg2d, point: IPoint2d): number {
  if (seg instanceof LineSeg2d) {
    return seg.getFootRatio(point);
  } else if (seg instanceof ArcSeg2d) {
    return seg.getExactFootRatio(point);
  }
  return 0;
}

export function getPointFromRatio(seg: IShapeSeg2d, ratio: number): IPoint2d {
  if (seg instanceof LineSeg2d) {
    return seg.getPointFromParameter(ratio);
  } else if (seg instanceof ArcSeg2d) {
    return seg.getPointFromRatio(ratio);
  }
  return { x: 0, y: 0 };
}

// 获取点在seg上的位置，返回0到1之间的值，点不在线段上则返回null
export function getPointPosOnSeg(seg: IShapeSeg2d, point: IPoint2d): number | null {
  if (seg instanceof LineSeg2d) {
    if (!seg.pointOnSegment(point)) return null;
    const total = seg.length;
    if (total === 0) return 0;
    return Point2dUtils.distanceTo(seg.start, point) / total;
  } else if (seg instanceof ArcSeg2d) {
    if (!seg.pointOnSegment(point)) return null;
    const angle = normalizeAngle(Math.atan2(point.y - seg.center.y, point.x - seg.center.x));
    const offset = seg.clockwise ? normalizeAngle(seg.startAngle - angle) : normalizeAngle(angle - seg.startAngle);
    return offset / seg.getSweepAngle();
  }

  return null;
}

//pos: 0到1之间的值，点不在线段上则返回null
export function getPointOnSeg(seg: IShapeSeg2d, pos: number): IPoint2d | null {
  if (pos < 0 || pos > 1) return null;

  if (seg instanceof LineSeg2d)
    return {
      x: seg.start.x + (seg.end.x - seg.start.x) * pos,
      y: seg.start.y + (seg.end.y - seg.start.y) * pos
    };

  if (seg instanceof ArcSeg2d) {
    const angle = seg.clockwise
      ? seg.startAngle - seg.getSweepAngle() * pos
      : seg.startAngle + seg.getSweepAngle() * pos;
    return {
      x: seg.center.x + seg.radius * Math.cos(angle),
      y: seg.center.y + seg.radius * Math.sin(angle)
    };
  }
  return null;
}

type ISegGeometryInfo =
  | {
      start: IPoint2d;
      end: IPoint2d;
      from?: IPoint2d;
      to?: IPoint2d;
      center?: IPoint2d;
      clockwise?: boolean;
    }
  | {
      start?: IPoint2d;
      end?: IPoint2d;
      from: IPoint2d;
      to: IPoint2d;
      center?: IPoint2d;
      clockwise?: boolean;
    };

/** 端点以 `start`/`end` 命名的那一支。收窄后两者均为必选，与 `from`/`to` 分支区分开 */
function isStartEndInfo(
  geometryInfo: ISegGeometryInfo
): geometryInfo is ISegGeometryInfo & { start: IPoint2d; end: IPoint2d } {
  return geometryInfo.start !== undefined && geometryInfo.end !== undefined;
}

/** 统一取出端点。联合类型保证 `start`/`end` 与 `from`/`to` 必有一套，因此无需非空断言 */
function getSegEnds(geometryInfo: ISegGeometryInfo): { start: IPoint2d; end: IPoint2d } {
  if (isStartEndInfo(geometryInfo)) return { start: geometryInfo.start, end: geometryInfo.end };
  return { start: geometryInfo.from, end: geometryInfo.to };
}

export function getShapeSeg(geometryInfo: ISegGeometryInfo) {
  const { start, end } = getSegEnds(geometryInfo);
  return isArc(geometryInfo)
    ? new ArcSeg2d({ start, end, center: geometryInfo.center, clockwise: geometryInfo.clockwise })
    : new LineSeg2d(start, end);
}

export function getShape(geometryInfo: ISegGeometryInfo) {
  const { start, end } = getSegEnds(geometryInfo);

  if (!geometryInfo.center) {
    return new Line2d({ points: { p1: start, p2: end } });
  }

  return new Circle2d({ center: geometryInfo.center, radius: Point2dUtils.distanceTo(start, geometryInfo.center) });
}

export function getLength(seg: IShapeSeg2d): number {
  if (seg instanceof LineSeg2d) {
    return seg.length;
  } else if (seg instanceof ArcSeg2d) {
    return seg.length;
  }
  return 0;
}

export function subtractSegments(mainSeg: IShapeSeg2d, subtractSegs: IShapeSeg2d[]): IShapeSeg2d[] {
  if (!subtractSegs.length) {
    return [mainSeg];
  }

  if (mainSeg instanceof LineSeg2d) {
    const lineSubtractSegs = subtractSegs.filter((seg) => seg instanceof LineSeg2d);
    return mainSeg.subtractSegments(lineSubtractSegs);
  }

  if (mainSeg instanceof ArcSeg2d) {
    const arcSubtractSegs = subtractSegs.filter((seg) => seg instanceof ArcSeg2d);
    return mainSeg.subtractSegments(arcSubtractSegs);
  }

  return [mainSeg];
}
